/**
 * Speech-to-text engine selection for the SDK's browser mic path.
 *
 * Historically `sttFromMic` (audioUtils.ts) was Azure-only: an Azure STS
 * token from the unauthenticated `/talk/get-speech-token` mint, then the
 * Azure Speech SDK recognizer. Camlin Speech ASR is now a first-class
 * alternative (Keith, 6 Aug 2026): recognition goes formally through the
 * Camlin Speech STT API (`POST /api/v1/asr/recognize`) with a short-lived,
 * tenant-bound `cat_…` token minted server-side — never a raw API key in
 * the browser — which is also how per-tenant lexicon biasing (word
 * injection) reaches the mic path, something the Azure path never had.
 *
 * Selection is a module-level registry rather than a prop thread: the mic
 * capture entry point (`sttFromMic`) sits under `useLiveListenControls`
 * behind many host surfaces, and every one of them should follow the site
 * config without new plumbing. A host (e.g. the Companion widget reading
 * its site config's `asr` block) calls `configureSttEngine(...)` once at
 * mount; unconfigured means Azure, exactly as before.
 *
 * Phrase lists (Aug 29 2026): avatar settings `speech.phrases` plus optional
 * host/SoW terms from `avatar:live:contextUpdate` are merged here so both
 * the Azure PhraseListGrammar path and the Camlin recognize headers read
 * one list.
 */

export interface SttEngineConfig {
  /** 'azure' (default, unchanged behaviour) or 'camlin'. */
  engine: 'azure' | 'camlin';
  /**
   * Camlin engine: URL returning `{token: "cat_…", expires_at}` JSON on
   * GET — a same-origin server route that holds the real API key and
   * calls the STT API's `POST /api/v1/asr/token`. Required for 'camlin'.
   * With `streaming`, the engine appends `?scope=stream` to mint a
   * `cst_…` streaming token instead.
   */
  tokenUrl?: string;
  /**
   * Camlin engine: the recognize endpoint (WAV in, transcript JSON out).
   * Defaults to `/api/v1/asr/recognize` resolved against the page origin,
   * which is correct when the page is served behind the Camlin Speech ALB.
   */
  recognizeUrl?: string;
  /** BCP-47-ish language tag forwarded as x-camlin-asr-language. */
  language?: string;
  /**
   * Camlin engine, live transcription (7 Aug 2026): stream mic PCM over
   * Socket.IO instead of batch-POSTing a finished WAV. Interim words
   * render as they're spoken; the server endpoints the turn on a natural
   * pause (`live_turn_final`). Config-switchable per site; batch stays
   * the default.
   */
  streaming?: boolean;
  /**
   * Streaming: origin of the Socket.IO live-STT server. Defaults to the
   * page origin (correct behind the Camlin Speech ALB); other sites set
   * `https://speech.camlinconnect.com.au`.
   */
  streamUrl?: string;
  /**
   * Static recognition phrases for this avatar (company name, SoW, …).
   * Applied as Azure PhraseListGrammar and Camlin context-words.
   */
  phrases?: string[];
  /**
   * When false, ignore host-supplied context terms. Unset / true merges
   * `setContextSpeechPhrases` into the live list.
   */
  includeContextTerms?: boolean;
}

export const SPEECH_PHRASE_MAX = 80;
export const SPEECH_PHRASE_MIN_LENGTH = 2;
export const SPEECH_PHRASE_MAX_LENGTH = 80;

const DEFAULT_CONFIG: SttEngineConfig = { engine: 'azure' };

let currentConfig: SttEngineConfig = DEFAULT_CONFIG;
let contextPhrases: string[] = [];

export function normalizeSpeechPhrases(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    if (typeof raw !== 'string') continue;
    const phrase = raw.trim().slice(0, SPEECH_PHRASE_MAX_LENGTH);
    if (phrase.length < SPEECH_PHRASE_MIN_LENGTH) continue;
    const key = phrase.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(phrase);
    if (out.length >= SPEECH_PHRASE_MAX) break;
  }
  return out;
}

export function configureSttEngine(config: SttEngineConfig | null | undefined): void {
  if (!config) {
    currentConfig = DEFAULT_CONFIG;
    return;
  }

  const phrases = normalizeSpeechPhrases(config.phrases);
  const includeContextTerms = config.includeContextTerms;
  const language = typeof config.language === 'string' && config.language.trim()
    ? config.language.trim()
    : undefined;

  if (config.engine !== 'camlin') {
    currentConfig = {
      engine: 'azure',
      phrases,
      includeContextTerms,
      language
    };
    return;
  }

  if (!config.tokenUrl) {
    console.warn('[sttEngine] camlin engine requested without tokenUrl — staying on azure (phrases still applied)');
    currentConfig = {
      engine: 'azure',
      phrases,
      includeContextTerms,
      language
    };
    return;
  }

  currentConfig = {
    engine: 'camlin',
    tokenUrl: config.tokenUrl,
    recognizeUrl: config.recognizeUrl || '/api/v1/asr/recognize',
    language,
    streaming: config.streaming === true,
    streamUrl: config.streamUrl,
    phrases,
    includeContextTerms
  };
  console.log('[sttEngine] Camlin Speech ASR engine selected', {
    tokenUrl: currentConfig.tokenUrl,
    recognizeUrl: currentConfig.recognizeUrl,
    streaming: currentConfig.streaming === true,
    phraseCount: phrases.length
  });
}

export function getSttEngineConfig(): SttEngineConfig {
  return currentConfig;
}

/** Host/SoW terms from contextUpdate — independent of engine selection. */
export function setContextSpeechPhrases(phrases: unknown): void {
  contextPhrases = normalizeSpeechPhrases(phrases);
}

export function getContextSpeechPhrases(): string[] {
  return contextPhrases.slice();
}

/**
 * Static avatar phrases plus (unless includeContextTerms is false) host
 * context terms. Azure PhraseListGrammar and Camlin recognize headers
 * both read this.
 */
export function getEffectiveSpeechPhrases(): string[] {
  const staticPhrases = currentConfig.phrases ?? [];
  if (currentConfig.includeContextTerms === false) {
    return staticPhrases.slice();
  }
  return normalizeSpeechPhrases([...staticPhrases, ...contextPhrases]);
}

/** Headers to attach to Camlin Speech recognize POSTs. */
export function camlinSpeechPhraseHeaders(): Record<string, string> {
  const phrases = getEffectiveSpeechPhrases();
  if (phrases.length === 0) return {};
  return { 'x-camlin-asr-context-words': JSON.stringify(phrases) };
}
